// src/pages/MessagesPage.js
import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, memo, useMemo, startTransition } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { useChatWebSocket } from '../context/ChatWebSocketContext';
import { getToken } from '../utils/auth';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import Header from '../components/Header';
import Footer from '../components/Footer';
import PillTabs from '../components/ui/PillTabs';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Send,
  MessageSquare,
  MessageCircle,
  Search,
  Check,
  CheckCheck,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL;
const CHAT_HISTORY_PAGE_SIZE = 50;
const SCROLL_BOTTOM_THRESHOLD = 80;
const api = axios.create({ baseURL: API_BASE_URL });

const isTokenValid = (token) => {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.exp > Date.now() / 1000;
  } catch { return false; }
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length >= 2) return `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`.toUpperCase();
  return name.charAt(0).toUpperCase();
};

function parseMessageTimestamp(value) {
  if (value == null) return null;
  if (typeof value !== 'string') return new Date(value);
  const s = value.trim();
  if (!s) return null;
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(s)) return new Date(s);
  return new Date(s.endsWith('Z') ? s : s + 'Z');
}

const sortMessagesByTime = (msgs) =>
  [...msgs].sort((a, b) => {
    const seqA = a.sequence != null ? Number(a.sequence) : null;
    const seqB = b.sequence != null ? Number(b.sequence) : null;
    if (seqA != null && seqB != null && seqA !== seqB) return seqA - seqB;
    const timeDiff =
      (parseMessageTimestamp(a.createdAt)?.getTime() ?? 0) -
      (parseMessageTimestamp(b.createdAt)?.getTime() ?? 0);
    if (timeDiff !== 0) return timeDiff;
    const idA = Number(a.id);
    const idB = Number(b.id);
    if (!Number.isNaN(idA) && !Number.isNaN(idB)) return idA - idB;
    return String(a.id).localeCompare(String(b.id));
  });

const formatMessage = (msg, userId) => {
  const isOutgoing = String(msg.sender_id) === String(userId);
  let isRead = false;
  if (msg.is_read !== undefined && msg.is_read !== null) {
    if (typeof msg.is_read === 'boolean') isRead = msg.is_read;
    else if (typeof msg.is_read === 'string') isRead = msg.is_read.toLowerCase() === 'true' || msg.is_read === '1';
    else if (typeof msg.is_read === 'number') isRead = msg.is_read === 1;
  }
  if (!isRead && (msg.read === true || msg.isRead === true)) isRead = true;
  const deliveryStatus = (
    isRead
      ? 'read'
      : (msg.delivery_status || msg.deliveryStatus || (msg.delivered_at || msg.deliveredAt ? 'delivered' : null) || 'sent')
  ).toLowerCase();
  const createdAtDate = parseMessageTimestamp(msg.created_at || msg.createdAt);
  return {
    id: msg.id,
    local_id: msg.local_id || msg.client_local_id || null,
    sequence: msg.sequence != null ? Number(msg.sequence) : null,
    message: msg.message_body ?? msg.message,
    sentTime: createdAtDate ? createdAtDate.toLocaleString() : '',
    sender: isOutgoing ? 'You' : msg.sender_type,
    direction: isOutgoing ? 'outgoing' : 'incoming',
    position: 'single',
    createdAt: msg.created_at || msg.createdAt,
    is_read: isRead || deliveryStatus === 'read',
    read_at: msg.read_at || msg.readAt || null,
    delivery_status: deliveryStatus,
    delivered_at: msg.delivered_at || msg.deliveredAt || null,
  };
};

const formatMessageTime = (dateString) => {
  const date = parseMessageTimestamp(dateString);
  if (!date || Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const formatScrollDateLabel = (dateString) => {
  const date = parseMessageTimestamp(dateString);
  if (!date || Number.isNaN(date.getTime())) return '';
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msgDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.round((today - msgDay) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return date.toLocaleDateString([], { weekday: 'long' });
  return date.toLocaleDateString([], {
    month: 'short',
    day: 'numeric',
    ...(date.getFullYear() !== now.getFullYear() ? { year: 'numeric' } : {}),
  });
};

const inferHasMoreMessages = (messageCount, serverHasMore) => {
  if (serverHasMore === true) return true;
  if (serverHasMore === false) return false;
  return messageCount >= CHAT_HISTORY_PAGE_SIZE;
};

function getDistanceFromBottom(container) {
  const { scrollTop, scrollHeight, clientHeight } = container;
  return scrollHeight - scrollTop - clientHeight;
}

const ChevronDownIcon = memo(() => (
  <ChevronDown width={22} height={22} strokeWidth={2.5} aria-hidden="true" />
));
ChevronDownIcon.displayName = 'ChevronDownIcon';

const ScrollToBottomButton = memo(({ visible, newMessageCount, onClick, isMobile }) => {
  if (!visible) return null;

  const hasBadge = newMessageCount > 0;
  const badgeLabel = newMessageCount > 99 ? '99+' : String(newMessageCount);

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={hasBadge ? `${badgeLabel} new messages, scroll to bottom` : 'Scroll to bottom'}
      style={{
        position: 'absolute',
        bottom: isMobile ? '14px' : '18px',
        right: isMobile ? '14px' : '20px',
        zIndex: 25,
        width: isMobile ? '46px' : '50px',
        height: isMobile ? '46px' : '50px',
        borderRadius: '50%',
        border: '1px solid #e5e7eb',
        backgroundColor: 'white',
        boxShadow: '0 6px 20px rgba(15, 23, 42, 0.14)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#0d9488',
        animation: 'scrollBtnIn 0.2s ease-out',
        transition: 'transform 0.15s ease, box-shadow 0.15s ease',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(15, 23, 42, 0.18)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 6px 20px rgba(15, 23, 42, 0.14)';
      }}
    >
      <ChevronDownIcon />
      {hasBadge && (
        <span
          style={{
            position: 'absolute',
            top: '-6px',
            right: '-6px',
            minWidth: '22px',
            height: '22px',
            padding: '0 6px',
            borderRadius: '999px',
            background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
            color: 'white',
            fontSize: '11px',
            fontWeight: '700',
            lineHeight: '22px',
            textAlign: 'center',
            boxShadow: '0 2px 8px rgba(13, 148, 136, 0.45)',
            border: '2px solid white',
            animation: 'badgePop 0.25s ease-out',
          }}
        >
          {badgeLabel}
        </span>
      )}
    </button>
  );
});
ScrollToBottomButton.displayName = 'ScrollToBottomButton';

const BackIcon = memo(() => (<ChevronLeft width={24} height={24} strokeWidth={2} aria-hidden="true" />));
BackIcon.displayName = 'BackIcon';
const SendIcon = memo(() => (<Send width={20} height={20} strokeWidth={2} fill="currentColor" aria-hidden="true" />));
SendIcon.displayName = 'SendIcon';
const MessageIcon = memo(({ size = 48 }) => (<MessageSquare width={size} height={size} color="#0d9488" strokeWidth={1.5} aria-hidden="true" />));
MessageIcon.displayName = 'MessageIcon';
const ChatBubbleIcon = memo(({ size = 40 }) => (<MessageCircle width={size} height={size} color="#0d9488" strokeWidth={1.5} aria-hidden="true" />));
ChatBubbleIcon.displayName = 'ChatBubbleIcon';
const SearchIcon = memo(() => (<Search width={18} height={18} color="#9ca3af" strokeWidth={2} aria-hidden="true" />));
SearchIcon.displayName = 'SearchIcon';
const CheckIcon = memo(({ deliveryStatus = 'sent', read = false }) => {
  const isRead = read || deliveryStatus === 'read';
  const isDelivered = isRead || deliveryStatus === 'delivered';
  const color = isRead ? '#3b82f6' : '#9ca3af';
  if (isDelivered) {
    return <CheckCheck width={16} height={16} color={color} strokeWidth={2.5} aria-hidden="true" />;
  }
  return <Check width={16} height={16} color="#9ca3af" strokeWidth={2.5} aria-hidden="true" />;
});
CheckIcon.displayName = 'CheckIcon';

const CustomAvatar = memo(({ name, size = 'default', isOnline = false }) => {
  const sizeStyles = { small: { width: '36px', height: '36px', fontSize: '13px' }, default: { width: '44px', height: '44px', fontSize: '15px' }, large: { width: '52px', height: '52px', fontSize: '18px' } };
  const colors = [{ bg: '#dbeafe', text: '#1d4ed8' }, { bg: '#dcfce7', text: '#15803d' }, { bg: '#fef3c7', text: '#b45309' }, { bg: '#fce7f3', text: '#be185d' }, { bg: '#e0e7ff', text: '#4338ca' }, { bg: '#ccfbf1', text: '#0f766e' }];
  const colorIndex = name ? name.charCodeAt(0) % colors.length : 0;
  const color = colors[colorIndex];
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ ...sizeStyles[size], backgroundColor: color.bg, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <span style={{ color: color.text, fontWeight: '600', letterSpacing: '0.5px' }}>{getInitials(name)}</span>
      </div>
      <div style={{ position: 'absolute', bottom: '2px', right: '2px', width: size === 'small' ? '10px' : '12px', height: size === 'small' ? '10px' : '12px', backgroundColor: isOnline ? '#22c55e' : '#9ca3af', borderRadius: '50%', border: '2px solid white', boxShadow: isOnline ? '0 0 0 2px rgba(34, 197, 94, 0.2)' : 'none' }} />
    </div>
  );
});
CustomAvatar.displayName = 'CustomAvatar';

const MessageBubble = memo(({ msg, isMobile }) => {
  const isOutgoing = msg.direction === 'outgoing';
  return (
    <div
      data-message-id={msg.id}
      data-created-at={msg.createdAt ?? ''}
      style={{ display: 'flex', justifyContent: isOutgoing ? 'flex-end' : 'flex-start', marginBottom: '4px', animation: 'fadeSlideIn 0.3s ease-out' }}
    >
      <div style={{ maxWidth: isMobile ? '85%' : '65%', padding: isMobile ? '10px 14px' : '12px 16px', borderRadius: isOutgoing ? '20px 20px 4px 20px' : '20px 20px 20px 4px', background: isOutgoing ? 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' : 'white', color: isOutgoing ? 'white' : '#1f2937', boxShadow: isOutgoing ? '0 2px 8px rgba(13, 148, 136, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', position: 'relative' }}>
        <p style={{ margin: 0, lineHeight: '1.5', fontSize: isMobile ? '14px' : '15px', wordBreak: 'break-word' }}>{msg.message}</p>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px', marginTop: '6px', fontSize: isMobile ? '10px' : '11px', opacity: isOutgoing ? 0.85 : 0.6 }}>
          <span>{formatMessageTime(msg.createdAt)}</span>
          {isOutgoing && (
            <span style={{ marginLeft: '4px', display: 'flex', alignItems: 'center' }}>
              <CheckIcon deliveryStatus={msg.delivery_status} read={msg.is_read} />
            </span>
          )}
        </div>
      </div>
    </div>
  );
}, (prevProps, nextProps) =>
  prevProps.msg.id === nextProps.msg.id &&
  prevProps.msg.is_read === nextProps.msg.is_read &&
  prevProps.msg.delivery_status === nextProps.msg.delivery_status &&
  prevProps.msg.sequence === nextProps.msg.sequence &&
  prevProps.isMobile === nextProps.isMobile
);
MessageBubble.displayName = 'MessageBubble';

const HeaderTypingIndicator = memo(() => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginLeft: '8px', color: '#0d9488', fontSize: '13px', fontWeight: '500', animation: 'fadeIn 0.2s ease-out' }}>
    <span style={{ display: 'inline-flex', gap: '3px', alignItems: 'center' }}>
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          style={{
            width: '6px',
            height: '6px',
            borderRadius: '50%',
            backgroundColor: '#0d9488',
            animation: `typingBounce 1.4s infinite ${i * 0.2}s`,
          }}
        />
      ))}
    </span>
    typing
  </span>
));
HeaderTypingIndicator.displayName = 'HeaderTypingIndicator';

const SearchInput = memo(({ onSearch, placeholder }) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState('');
  const debounceRef = useRef(null);
  const handleChange = useCallback((e) => { const value = e.target.value; setLocalValue(value); if (debounceRef.current) clearTimeout(debounceRef.current); debounceRef.current = setTimeout(() => onSearch(value), 150); }, [onSearch]);
  useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);
  return (
    <div style={{ position: 'relative' }}>
      <div style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}><SearchIcon /></div>
      <input ref={inputRef} type="text" placeholder={placeholder} value={localValue} onChange={handleChange} style={{ width: '100%', padding: '12px 14px 12px 42px', borderRadius: '12px', border: '2px solid #e5e7eb', fontSize: '14px', outline: 'none', boxSizing: 'border-box', backgroundColor: 'white', transition: 'border-color 0.2s, box-shadow 0.2s' }} onFocus={(e) => { e.target.style.borderColor = '#0d9488'; e.target.style.boxShadow = '0 0 0 3px rgba(13, 148, 136, 0.1)'; }} onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
    </div>
  );
});
SearchInput.displayName = 'SearchInput';

const MessageInput = memo(({ onSend, onTyping, placeholder, disabled, isMobile }) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState('');
  const lastTypingSentRef = useRef(0);
  const typingTimeoutRef = useRef(null);
  const handleChange = useCallback((e) => { const value = e.target.value; setLocalValue(value); if (onTyping) { const now = Date.now(); const trimmed = value.trim(); if (now - lastTypingSentRef.current > 300) { onTyping(trimmed.length > 0); lastTypingSentRef.current = now; } if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current); if (trimmed.length > 0) { typingTimeoutRef.current = setTimeout(() => onTyping(false), 2000); } } }, [onTyping]);
  const handleSend = useCallback(() => { if (localValue.trim() && !disabled) { onSend(localValue.trim()); setLocalValue(''); if (onTyping) { onTyping(false); lastTypingSentRef.current = 0; } if (typingTimeoutRef.current) { clearTimeout(typingTimeoutRef.current); typingTimeoutRef.current = null; } requestAnimationFrame(() => inputRef.current?.focus()); } }, [localValue, disabled, onSend, onTyping]);
  const handleKeyDown = useCallback((e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }, [handleSend]);
  useEffect(() => () => { if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current); }, []);
  useEffect(() => { if (!disabled && inputRef.current) { const timer = setTimeout(() => inputRef.current?.focus(), 100); return () => clearTimeout(timer); } }, [disabled]);
  const canSend = localValue.trim() && !disabled;
  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', padding: '4px', backgroundColor: '#f9fafb', borderRadius: '28px', border: '2px solid #e5e7eb', transition: 'border-color 0.2s, box-shadow 0.2s' }}>
      <input ref={inputRef} type="text" value={localValue} onChange={handleChange} onKeyDown={handleKeyDown} placeholder={placeholder} disabled={disabled} autoComplete="off" style={{ flex: 1, padding: isMobile ? '12px 16px' : '14px 18px', borderRadius: '24px', border: 'none', fontSize: isMobile ? '15px' : '16px', outline: 'none', backgroundColor: 'transparent', boxSizing: 'border-box', minWidth: 0 }} />
      <button onClick={handleSend} disabled={!canSend} onMouseDown={(e) => e.preventDefault()} style={{ padding: isMobile ? '12px' : '14px', width: isMobile ? '48px' : '52px', height: isMobile ? '48px' : '52px', borderRadius: '50%', border: 'none', background: canSend ? 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)' : '#e5e7eb', color: 'white', cursor: canSend ? 'pointer' : 'not-allowed', transition: 'all 0.2s', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: canSend ? '0 4px 12px rgba(13, 148, 136, 0.4)' : 'none', transform: canSend ? 'scale(1)' : 'scale(0.95)' }}><SendIcon /></button>
    </div>
  );
});
MessageInput.displayName = 'MessageInput';

const PatientListItem = memo(({ patient, isSelected, isOnline, onClick, isMobile, unreadCount = 0 }) => {
  const hasUnread = unreadCount > 0;
  return (
    <div onClick={onClick} style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: isMobile ? '14px 16px' : '16px 20px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6', backgroundColor: isSelected ? '#f0fdfa' : hasUnread ? '#fefce8' : 'white', borderLeft: `4px solid ${isSelected ? '#0d9488' : hasUnread ? '#eab308' : 'transparent'}`, transition: 'background-color 0.2s ease, border-color 0.2s ease' }} onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = hasUnread ? '#fef9c3' : '#f9fafb'; }} onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.backgroundColor = hasUnread ? '#fefce8' : 'white'; }}>
      <CustomAvatar name={patient.name} size={isMobile ? 'small' : 'default'} isOnline={isOnline} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ fontWeight: hasUnread ? '700' : '600', color: '#1f2937', margin: 0, fontSize: isMobile ? '14px' : '15px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{patient.name}</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginTop: '4px', flexWrap: 'wrap' }}>
          <p style={{ fontSize: isMobile ? '12px' : '13px', color: '#6b7280', margin: 0 }}>MRN: {patient.MRN}</p>
          {isOnline && <span style={{ fontSize: '11px', color: '#22c55e', fontWeight: '500' }}>• Online</span>}
        </div>
      </div>
      {hasUnread && (<div style={{ backgroundColor: '#dc2626', color: 'white', borderRadius: '9999px', minWidth: '26px', height: '26px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', padding: '0 8px', flexShrink: 0, boxShadow: '0 2px 6px rgba(220, 38, 38, 0.35)' }}>{unreadCount > 99 ? '99+' : unreadCount}</div>)}
      <ChevronRight width={20} height={20} color={isSelected ? '#0d9488' : '#d1d5db'} strokeWidth={2} style={{ flexShrink: 0, transition: 'stroke 0.2s' }} aria-hidden="true" />
    </div>
  );
});
PatientListItem.displayName = 'PatientListItem';

const PatientListFilter = memo(({ value, onChange, allCount, onlineCount }) => (
  <PillTabs
    value={value}
    onChange={onChange}
    layoutId="messages-patient-filter"
    fullWidth
    size="sm"
    options={[
      { id: 'all', label: 'All', count: allCount },
      { id: 'online', label: 'Online', count: onlineCount },
    ]}
  />
));
PatientListFilter.displayName = 'PatientListFilter';

const PatientListSectionHeader = memo(({ title, count, variant = 'online' }) => {
  const isOnline = variant === 'online';
  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '8px 20px 10px',
        backgroundColor: isOnline ? '#f0fdfa' : '#f9fafb',
        borderBottom: '1px solid #e5e7eb',
        borderTop: isOnline ? 'none' : '1px solid #e5e7eb',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        {isOnline && (
          <span
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: '#22c55e',
              boxShadow: '0 0 0 3px rgba(34, 197, 94, 0.18)',
            }}
          />
        )}
        <span style={{ fontSize: '12px', fontWeight: '700', color: isOnline ? '#0f766e' : '#6b7280', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
          {title}
        </span>
      </div>
      <span
        style={{
          fontSize: '11px',
          fontWeight: '700',
          color: isOnline ? '#0f766e' : '#9ca3af',
          backgroundColor: isOnline ? 'rgba(13, 148, 136, 0.1)' : '#f3f4f6',
          padding: '3px 8px',
          borderRadius: '999px',
        }}
      >
        {count}
      </span>
    </div>
  );
});
PatientListSectionHeader.displayName = 'PatientListSectionHeader';

const ConnectionBadge = memo(({ status, isPatientOnline }) => {
  const getConfig = () => { if (isPatientOnline) return { bg: '#dcfce7', color: '#15803d', text: 'Online', dot: '#22c55e' }; if (status === 'connected') return { bg: '#f3f4f6', color: '#6b7280', text: 'Offline', dot: '#9ca3af' }; if (status === 'connecting') return { bg: '#fef3c7', color: '#b45309', text: 'Connecting...', dot: '#f59e0b' }; return { bg: '#fee2e2', color: '#dc2626', text: 'Disconnected', dot: '#ef4444' }; };
  const config = getConfig();
  return (<div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderRadius: '20px', backgroundColor: config.bg, fontSize: '12px', fontWeight: '500', color: config.color }}><div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: config.dot, animation: status === 'connecting' ? 'pulse 1.5s infinite' : isPatientOnline ? 'pulse 2s infinite' : 'none' }} />{config.text}</div>);
});
ConnectionBadge.displayName = 'ConnectionBadge';

const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({ width: typeof window !== 'undefined' ? window.innerWidth : 1200, height: typeof window !== 'undefined' ? window.innerHeight : 800 });
  useEffect(() => { let timeoutId; const handleResize = () => { clearTimeout(timeoutId); timeoutId = setTimeout(() => setWindowSize({ width: window.innerWidth, height: window.innerHeight }), 100); }; window.addEventListener('resize', handleResize); handleResize(); return () => { window.removeEventListener('resize', handleResize); clearTimeout(timeoutId); }; }, []);
  return windowSize;
};

// MESSAGES PAGE

function MessagesPage() {
  const { user } = useAuth();
  const {
    unreadByConversation,
    clearUnreadForConversation,
    hydrateUnread,
  } = useNotification();
  const { width: windowWidth, height: windowHeight } = useWindowSize();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [patientListFilter, setPatientListFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoadingOlder, setIsLoadingOlder] = useState(false);
  const [scrollDateLabel, setScrollDateLabel] = useState('');
  const [showScrollDate, setShowScrollDate] = useState(false);
  const [showScrollDownButton, setShowScrollDownButton] = useState(false);
  const [pendingNewMessageCount, setPendingNewMessageCount] = useState(0);
  const [allConversations, setAllConversations] = useState(new Map());

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isUserScrollingRef = useRef(false);
  const messageBatchRef = useRef([]);
  const batchTimeoutRef = useRef(null);
  const readMessagesRef = useRef(new Set());
  const allConversationsRef = useRef(new Map());
  const formatMessageCacheRef = useRef(new Map());
  const subscribedConversationsRef = useRef(new Set());
  const markedAsReadRef = useRef(new Set());
  const readObserverRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const paginationRef = useRef(new Map());
  const pendingHistoryRef = useRef(null);
  const loadingOlderRef = useRef(false);
  const scrollIntentRef = useRef(null);
  const userScrolledAwayFromBottomRef = useRef(false);
  const scrollDateHideTimeoutRef = useRef(null);

  const hasRequestedConversationsRef = useRef(false);
  const conversationIdRef = useRef(conversationId);
  const messagesRef = useRef(messages);
  const sequenceCursorRef = useRef(new Map());
  const pendingLocalIdsRef = useRef(new Set());
  const deliveredSentRef = useRef(new Set());

  const USER_ID = user?.user_id;
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;
  const headerHeight = 64;
  const footerHeight = 60;
  const mainPadding = isMobile ? 16 : 24;
  const containerHeight = windowHeight - headerHeight - footerHeight - mainPadding * 2;

  const {
    sendJsonMessage,
    isConnected,
    connectionStatus,
    connectionEpoch,
    isUserOnline,
    refreshOnlineUsers,
    subscribeSocketEvents,
    setViewingConversationId,
  } = useChatWebSocket();

  const userTypeRef = useRef(null);
  useEffect(() => { userTypeRef.current = user?.user_type; }, [user?.user_type]);
  useEffect(() => { conversationIdRef.current = conversationId; }, [conversationId]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  useEffect(() => {
    if (!isConnected) {
      hasRequestedConversationsRef.current = false;
    }
  }, [isConnected]);

  useEffect(() => {
    hasRequestedConversationsRef.current = false;
  }, [connectionEpoch]);

  useEffect(() => {
    if (!isConnected || hasRequestedConversationsRef.current) return;
    if (!user?.user_type) return;

    hasRequestedConversationsRef.current = true;

    const userType = (user.user_type || '').toLowerCase();
    if (userType === 'clinician' || userType === 'doctor' || userType === 'nurse') {
      sendJsonMessage({ type: 'history_all_patients', limit_per_conversation: CHAT_HISTORY_PAGE_SIZE });
    } else if (userType === 'patient') {
      sendJsonMessage({ type: 'history_all_doctors', limit_per_conversation: CHAT_HISTORY_PAGE_SIZE });
    }
  }, [isConnected, user?.user_type, sendJsonMessage, connectionEpoch]);

  useEffect(() => {
    if (isConnected) refreshOnlineUsers();
  }, [isConnected, connectionEpoch, refreshOnlineUsers]);

  useEffect(() => {
    setViewingConversationId(conversationId ?? null);
    return () => setViewingConversationId(null);
  }, [conversationId, setViewingConversationId]);

  const isPatientOnline = useCallback(
    (patientMRNOrPatient) => isUserOnline(patientMRNOrPatient),
    [isUserOnline]
  );

  const requestScrollToBottom = useCallback(() => {
    scrollIntentRef.current = { type: 'bottom' };
  }, []);

  const resetScrollDownUi = useCallback(() => {
    setShowScrollDownButton(false);
    setPendingNewMessageCount(0);
  }, []);

  const syncScrollPositionState = useCallback((container) => {
    if (!container) return;
    const distanceFromBottom = getDistanceFromBottom(container);
    const awayFromBottom = distanceFromBottom > SCROLL_BOTTOM_THRESHOLD;
    userScrolledAwayFromBottomRef.current = awayFromBottom;
    isUserScrollingRef.current = awayFromBottom;
    setShowScrollDownButton(awayFromBottom);
    if (!awayFromBottom) {
      setPendingNewMessageCount(0);
    }
  }, []);

  const scrollToBottomNow = useCallback(() => {
    const container = messagesContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
    userScrolledAwayFromBottomRef.current = false;
    isUserScrollingRef.current = false;
    resetScrollDownUi();
  }, [resetScrollDownUi]);

  const requestScrollPreserve = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    scrollIntentRef.current = {
      type: 'preserve',
      scrollHeight: container.scrollHeight,
      scrollTop: container.scrollTop,
    };
  }, []);

  useLayoutEffect(() => {
    const intent = scrollIntentRef.current;
    if (!intent) return;

    const container = messagesContainerRef.current;
    if (!container || messages.length === 0) return;

    scrollIntentRef.current = null;

    const applyIntent = () => {
      if (intent.type === 'bottom') {
        container.scrollTop = container.scrollHeight;
        userScrolledAwayFromBottomRef.current = false;
        isUserScrollingRef.current = false;
        setShowScrollDownButton(false);
        setPendingNewMessageCount(0);
      } else if (intent.type === 'preserve') {
        container.scrollTop = container.scrollHeight - intent.scrollHeight + intent.scrollTop;
      }
    };

    applyIntent();
    requestAnimationFrame(applyIntent);
  }, [messages, conversationId]);

  const loadOlderMessages = useCallback(() => {
    const cid = conversationIdRef.current;
    if (!cid || !isConnected || loadingOlderRef.current) return;

    const messageCount = messagesRef.current.length;
    const pag = paginationRef.current.get(String(cid));
    if (!inferHasMoreMessages(messageCount, pag?.hasMore)) return;

    const oldest = messagesRef.current[0];
    if (!oldest?.id) return;

    loadingOlderRef.current = true;
    setIsLoadingOlder(true);
    pendingHistoryRef.current = { conversationId: cid, before_id: oldest.id, mode: 'older' };
    sendJsonMessage({
      type: 'history',
      conversation_id: cid,
      limit: CHAT_HISTORY_PAGE_SIZE,
      before_id: oldest.id,
    });
  }, [isConnected, sendJsonMessage]);

  const updateScrollDateLabel = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container || messagesRef.current.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const anchorY = containerRect.top + 72;
    const elements = container.querySelectorAll('[data-message-id]');
    let visibleMessage = null;

    for (const el of elements) {
      const rect = el.getBoundingClientRect();
      if (rect.bottom >= anchorY) {
        const messageId = el.getAttribute('data-message-id');
        visibleMessage = messagesRef.current.find((m) => String(m.id) === String(messageId));
        break;
      }
    }

    if (!visibleMessage?.createdAt) return;

    const label = formatScrollDateLabel(visibleMessage.createdAt);
    if (!label) return;

    setScrollDateLabel(label);
    setShowScrollDate(true);

    if (scrollDateHideTimeoutRef.current) clearTimeout(scrollDateHideTimeoutRef.current);
    scrollDateHideTimeoutRef.current = setTimeout(() => {
      setShowScrollDate(false);
    }, 1400);
  }, []);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container || !selectedPatient) return undefined;

    const handleScroll = () => {
      syncScrollPositionState(container);
      updateScrollDateLabel();
      if (userScrolledAwayFromBottomRef.current && container.scrollTop < 120) {
        loadOlderMessages();
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollDateHideTimeoutRef.current) {
        clearTimeout(scrollDateHideTimeoutRef.current);
        scrollDateHideTimeoutRef.current = null;
      }
    };
  }, [loadOlderMessages, updateScrollDateLabel, syncScrollPositionState, selectedPatient, conversationId, messages.length]);

  const formatMessageMemo = useCallback((msg, userId) => {
    const cacheKey = `${msg.id}_${userId}`;
    if (formatMessageCacheRef.current.has(cacheKey)) return formatMessageCacheRef.current.get(cacheKey);
    const formatted = formatMessage(msg, userId);
    formatMessageCacheRef.current.set(cacheKey, formatted);
    if (formatMessageCacheRef.current.size > 1000) { const firstKey = formatMessageCacheRef.current.keys().next().value; formatMessageCacheRef.current.delete(firstKey); }
    return formatted;
  }, []);

  const noteMessageSequence = useCallback((conversationIdValue, sequence) => {
    if (conversationIdValue == null || sequence == null) return;
    const cid = String(conversationIdValue);
    const seq = Number(sequence);
    if (Number.isNaN(seq)) return;
    const prev = sequenceCursorRef.current.get(cid) ?? 0;
    if (seq > prev) sequenceCursorRef.current.set(cid, seq);
  }, []);

  const noteFormattedSequences = useCallback((conversationIdValue, msgs) => {
    if (!conversationIdValue || !Array.isArray(msgs)) return;
    msgs.forEach((msg) => noteMessageSequence(conversationIdValue, msg.sequence));
  }, [noteMessageSequence]);

  const sendResyncCursor = useCallback(() => {
    if (!isConnected) return;
    const cursor = {};
    sequenceCursorRef.current.forEach((seq, cid) => {
      if (cid != null && seq != null) cursor[cid] = seq;
    });
    if (Object.keys(cursor).length === 0) return;
    sendJsonMessage({ type: 'resync', cursor });
  }, [isConnected, sendJsonMessage]);

  const ackDelivered = useCallback((messageId) => {
    if (!isConnected || messageId == null) return;
    const key = String(messageId);
    if (deliveredSentRef.current.has(key)) return;
    if (String(messageId).startsWith('local_')) return;
    deliveredSentRef.current.add(key);
    const numericId = Number(messageId);
    sendJsonMessage({
      type: 'delivered',
      message_id: Number.isNaN(numericId) ? messageId : numericId,
    });
  }, [isConnected, sendJsonMessage]);

  const upsertConversationMessage = useCallback((conversationIdValue, formatted) => {
    if (!conversationIdValue || !formatted) return;
    startTransition(() => {
      setAllConversations((prev) => {
        const updated = new Map(prev);
        const conv = updated.get(conversationIdValue);
        const sameMessage = (m) =>
          String(m.id) === String(formatted.id) ||
          (formatted.local_id && (String(m.local_id) === String(formatted.local_id) || String(m.id) === String(formatted.local_id)));
        if (conv) {
          const existingIdx = (conv.messages || []).findIndex(sameMessage);
          let nextMessages;
          if (existingIdx >= 0) {
            nextMessages = [...conv.messages];
            nextMessages[existingIdx] = { ...nextMessages[existingIdx], ...formatted };
          } else {
            nextMessages = [...(conv.messages || []), formatted];
          }
          updated.set(conversationIdValue, {
            ...conv,
            messages: sortMessagesByTime(nextMessages),
          });
        } else {
          updated.set(conversationIdValue, {
            conversation_id: conversationIdValue,
            messages: [formatted],
          });
        }
        allConversationsRef.current = updated;
        return updated;
      });
    });
  }, []);

  const mergeMessagesByIdentity = useCallback((prev, incomingFormatted) => {
    const sameMessage = (m) =>
      String(m.id) === String(incomingFormatted.id) ||
      (incomingFormatted.local_id &&
        (String(m.local_id) === String(incomingFormatted.local_id) ||
          String(m.id) === String(incomingFormatted.local_id)));
    const idx = prev.findIndex(sameMessage);
    if (idx >= 0) {
      const next = [...prev];
      next[idx] = { ...next[idx], ...incomingFormatted };
      return sortMessagesByTime(next);
    }
    return sortMessagesByTime([...prev, incomingFormatted]);
  }, []);

  const handleNewMessage = useCallback((newMsg) => {
    const formatted = formatMessageMemo(newMsg, USER_ID);
    const msgConversationId = newMsg.conversation_id;
    noteMessageSequence(msgConversationId, formatted.sequence);

    if (
      formatted.direction === 'incoming' &&
      formatted.id != null &&
      !String(formatted.id).startsWith('local_')
    ) {
      ackDelivered(formatted.id);
    }

    const existsAlready = messagesRef.current.some(
      (m) =>
        String(m.id) === String(formatted.id) ||
        (formatted.local_id &&
          (String(m.local_id) === String(formatted.local_id) ||
            String(m.id) === String(formatted.local_id)))
    );
    if (existsAlready) {
      startTransition(() => {
        setMessages((prev) => mergeMessagesByIdentity(prev, formatted));
      });
      if (msgConversationId) upsertConversationMessage(msgConversationId, formatted);
      return;
    }

    const msgId = String(formatted.id);
    if (messageBatchRef.current.some((m) => String(m.id) === msgId || (formatted.local_id && String(m.client_local_id || m.local_id) === String(formatted.local_id)))) {
      return;
    }
    messageBatchRef.current.push(newMsg);
    if (batchTimeoutRef.current) clearTimeout(batchTimeoutRef.current);
    batchTimeoutRef.current = setTimeout(() => {
      const batch = [...messageBatchRef.current];
      messageBatchRef.current = [];
      const existing = messagesRef.current;
      const toAdd = [];
      batch.forEach((msg) => {
        const item = formatMessageMemo(msg, USER_ID);
        noteMessageSequence(msg.conversation_id, item.sequence);
        const duplicate = existing.some(
          (m) =>
            String(m.id) === String(item.id) ||
            (item.local_id &&
              (String(m.local_id) === String(item.local_id) || String(m.id) === String(item.local_id)))
        ) || toAdd.some(
          (m) =>
            String(m.id) === String(item.id) ||
            (item.local_id && String(m.local_id) === String(item.local_id))
        );
        if (!duplicate) toAdd.push(item);
        if (msg.conversation_id) upsertConversationMessage(msg.conversation_id, item);
        if (item.direction === 'incoming' && item.id != null && !String(item.id).startsWith('local_')) {
          ackDelivered(item.id);
        }
      });
      if (toAdd.length === 0) return;

      if (userScrolledAwayFromBottomRef.current) {
        setPendingNewMessageCount((count) => count + toAdd.length);
        setShowScrollDownButton(true);
      } else {
        requestScrollToBottom();
      }

      startTransition(() => {
        setMessages((prev) => sortMessagesByTime([...prev, ...toAdd]));
      });
    }, 16);
  }, [USER_ID, formatMessageMemo, requestScrollToBottom, noteMessageSequence, ackDelivered, upsertConversationMessage, mergeMessagesByIdentity]);

  useEffect(() => {
    if (!conversationId || !isConnected || messages.length === 0) {
      if (readObserverRef.current) { readObserverRef.current.disconnect(); readObserverRef.current = null; }
      return;
    }
    if (!readObserverRef.current) {
      readObserverRef.current = new IntersectionObserver((entries) => {
        const toMark = [];
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const messageIdAttr = entry.target.dataset.messageId;
          if (!messageIdAttr) return;
          const message = messagesRef.current.find((m) => String(m.id) === String(messageIdAttr));
          if (!message || markedAsReadRef.current.has(message.id)) return;
          if (message.direction === 'incoming' && !message.is_read) { markedAsReadRef.current.add(message.id); toMark.push(message.id); }
        });
        if (toMark.length > 0) {
          toMark.forEach((id) => { const numericId = typeof id === 'number' ? id : Number(id); sendJsonMessage({ type: 'read', message_id: Number.isNaN(numericId) ? id : numericId }); });
        }
      }, { threshold: 0.5, rootMargin: '50px' });
    }
    requestAnimationFrame(() => { document.querySelectorAll('[data-message-id]').forEach((el) => { readObserverRef.current?.observe(el); }); });
    return () => { readObserverRef.current?.disconnect(); };
  }, [messages.length, conversationId, isConnected, sendJsonMessage]);

  const handleSocketEvent = useCallback((event) => {
    const eventType = (event?.type || '').toLowerCase();
    const data = event?.data || event;

    switch (eventType) {
      case 'connected':
        sendResyncCursor();
        break;

      case 'online_users':
      case 'presence_list':
      case 'presence_sync':
      case 'user_online':
      case 'user_offline':
        break;

      case 'subscribed': {
        const subConvoId = data?.conversation_id;
        if (subConvoId) subscribedConversationsRef.current.add(subConvoId);
        break;
      }

      case 'conversation_created':
      case 'conversation_ready': {
        const convoId = data?.conversation_id;
        if (convoId) {
          setConversationId(convoId);
          if (eventType === 'conversation_ready') {
            startTransition(() => {
              const msgs = sortMessagesByTime(
                (data?.messages || []).map((msg) => {
                  const formatted = formatMessageMemo(msg, USER_ID);
                  if (formatted.is_read && formatted.direction === 'outgoing') {
                    readMessagesRef.current.add(String(formatted.id));
                  }
                  return formatted;
                })
              );
              noteFormattedSequences(convoId, msgs);
              setMessages((prev) => (msgs.length > 0 ? msgs : prev));
              clearUnreadForConversation(convoId);
              requestScrollToBottom();
              paginationRef.current.set(String(convoId), {
                hasMore: inferHasMoreMessages(msgs.length, data?.has_more),
                oldestId: data?.oldest_id ?? (msgs[0]?.id ?? null),
              });
              setAllConversations((prev) => {
                const updated = new Map(prev);
                const existing = updated.get(convoId) || {};
                updated.set(convoId, {
                  ...existing,
                  conversation_id: convoId,
                  messages: msgs.length > 0 ? msgs : (existing.messages || []),
                  PMRN: data.PMRN ?? existing.PMRN,
                  name: data.name ?? existing.name,
                });
                allConversationsRef.current = updated;
                return updated;
              });
            });
          } else {
            sendJsonMessage({
              type: 'history',
              conversation_id: convoId,
              limit: CHAT_HISTORY_PAGE_SIZE,
            });
          }
        }
        break;
      }

      case 'history': {
        const responseConvoId = data?.conversation_id;
        if (
          responseConvoId != null &&
          conversationIdRef.current != null &&
          String(responseConvoId) !== String(conversationIdRef.current)
        ) {
          break;
        }

        const pending = pendingHistoryRef.current;
        const isOlderPage =
          data?.before_id != null ||
          (pending?.mode === 'older' &&
            pending?.conversationId != null &&
            String(pending.conversationId) === String(responseConvoId));

        if (isOlderPage) pendingHistoryRef.current = null;
        loadingOlderRef.current = false;
        setIsLoadingOlder(false);

        const formattedMsgs = sortMessagesByTime(
          (data?.messages || []).map((msg) => {
            const formatted = formatMessageMemo(msg, USER_ID);
            if (formatted.is_read && formatted.direction === 'outgoing') {
              readMessagesRef.current.add(String(formatted.id));
            }
            return formatted;
          })
        );
        if (responseConvoId) noteFormattedSequences(responseConvoId, formattedMsgs);

        if (responseConvoId) {
          const mergedCount = isOlderPage
            ? formattedMsgs.filter(
                (m) => !messagesRef.current.some((existing) => String(existing.id) === String(m.id))
              ).length + messagesRef.current.length
            : formattedMsgs.length;
          paginationRef.current.set(String(responseConvoId), {
            hasMore: isOlderPage && formattedMsgs.length === 0
              ? false
              : inferHasMoreMessages(mergedCount, data?.has_more),
            oldestId: data?.oldest_id ?? null,
          });
        }

        startTransition(() => {
          if (isOlderPage) {
            requestScrollPreserve();
            setMessages((prev) => {
              const existingIds = new Set(prev.map((m) => String(m.id)));
              const older = formattedMsgs.filter((m) => !existingIds.has(String(m.id)));
              return sortMessagesByTime([...older, ...prev]);
            });
            return;
          }

          const activeConvoId = responseConvoId ?? conversationIdRef.current;
          if (activeConvoId) clearUnreadForConversation(activeConvoId);

          if (messagesRef.current.length === 0) {
            setMessages(formattedMsgs);
            requestScrollToBottom();
            return;
          }

          setMessages((prev) => {
            const byId = new Map(prev.map((m) => [String(m.id), m]));
            formattedMsgs.forEach((m) => byId.set(String(m.id), m));
            return sortMessagesByTime([...byId.values()]);
          });
        });
        break;
      }

      case 'history_all_patients':
      case 'history_all_doctors': {
        const conversations = data?.conversations || [];
        const conversationsMap = new Map();
        const unreadEntries = [];
        const activeConversationId = conversationIdRef.current;

        startTransition(() => {
          conversations.forEach((conv) => {
            if (!conv.conversation_id) return;
            const msgs = sortMessagesByTime(
              (conv.messages || []).map((msg) => {
                const formatted = formatMessageMemo(msg, USER_ID);
                if (formatted.is_read && formatted.direction === 'outgoing') {
                  readMessagesRef.current.add(String(formatted.id));
                }
                return formatted;
              })
            );
            noteFormattedSequences(conv.conversation_id, msgs);

            const isCurrentConversation =
              activeConversationId != null &&
              String(conv.conversation_id) === String(activeConversationId);
            const unreadCount = isCurrentConversation
              ? 0
              : msgs.filter((m) => m.direction === 'incoming' && !m.is_read).length;

            if (unreadCount > 0) {
              unreadEntries.push({
                conversationId: conv.conversation_id,
                count: unreadCount,
                displayName: String(
                  conv.patient_info?.patient_name ??
                    conv.doctor_info?.display_name ??
                    conv.conversation_name ??
                    ''
                ),
              });
            }

            const convData = {
              conversation_id: conv.conversation_id,
              messages: msgs,
              conversation_name: conv.conversation_name,
              updated_at: conv.updated_at,
              has_more: inferHasMoreMessages(msgs.length, conv.has_more),
              oldest_id: conv.oldest_id ?? null,
            };
            if (eventType === 'history_all_patients') convData.patient_info = conv.patient_info;
            else convData.doctor_info = conv.doctor_info;
            conversationsMap.set(conv.conversation_id, convData);
            paginationRef.current.set(String(conv.conversation_id), {
              hasMore: inferHasMoreMessages(msgs.length, conv.has_more),
              oldestId: conv.oldest_id ?? (msgs[0]?.id ?? null),
            });
          });

          setAllConversations(conversationsMap);
          allConversationsRef.current = conversationsMap;
          hydrateUnread(unreadEntries, { replace: true });

          if (activeConversationId && conversationsMap.has(activeConversationId)) {
            setMessages(conversationsMap.get(activeConversationId).messages);
            requestScrollToBottom();
          }
        });
        sendResyncCursor();
        break;
      }

      case 'message': {
        const msgConversationId = data?.conversation_id;
        const isActiveConversation =
          msgConversationId != null &&
          conversationIdRef.current != null &&
          String(msgConversationId) === String(conversationIdRef.current);

        if (isActiveConversation) {
          handleNewMessage(data);
        } else if (msgConversationId) {
          const formatted = formatMessageMemo(data, USER_ID);
          noteMessageSequence(msgConversationId, formatted.sequence);
          if (formatted.direction === 'incoming') ackDelivered(formatted.id);
          upsertConversationMessage(msgConversationId, formatted);
        }
        break;
      }

      case 'message_ack': {
        const localId = data?.local_id;
        const serverId = data?.server_message_id;
        const sequence = data?.sequence;
        const ackConversationId = data?.conversation_id ?? conversationIdRef.current;
        if (localId != null) pendingLocalIdsRef.current.delete(String(localId));
        if (serverId != null) noteMessageSequence(ackConversationId, sequence);
        const applyAck = (msg) => {
          const matches =
            (localId != null &&
              (String(msg.local_id) === String(localId) || String(msg.id) === String(localId))) ||
            (serverId != null && String(msg.id) === String(serverId));
          if (!matches) return msg;
          formatMessageCacheRef.current.delete(`${msg.id}_${USER_ID}`);
          return {
            ...msg,
            id: serverId ?? msg.id,
            local_id: localId ?? msg.local_id,
            sequence: sequence != null ? Number(sequence) : msg.sequence,
            delivery_status: msg.is_read ? 'read' : (msg.delivery_status === 'delivered' ? 'delivered' : 'sent'),
          };
        };
        startTransition(() => {
          setMessages((prev) => sortMessagesByTime(prev.map(applyAck)));
          if (ackConversationId) {
            setAllConversations((prev) => {
              const conv = prev.get(ackConversationId);
              if (!conv?.messages) return prev;
              const updated = new Map(prev);
              updated.set(ackConversationId, {
                ...conv,
                messages: sortMessagesByTime(conv.messages.map(applyAck)),
              });
              allConversationsRef.current = updated;
              return updated;
            });
          }
        });
        break;
      }

      case 'delivered': {
        const deliveredMessageId = String(data?.message_id);
        const deliveredConversationId = data?.conversation_id;
        const deliveredAt = data?.delivered_at || new Date().toISOString();
        noteMessageSequence(deliveredConversationId, data?.sequence);
        formatMessageCacheRef.current.delete(`${deliveredMessageId}_${USER_ID}`);
        const applyDelivered = (msg) => {
          if (String(msg.id) !== deliveredMessageId) return msg;
          if (msg.is_read || msg.delivery_status === 'read') return msg;
          return {
            ...msg,
            delivery_status: 'delivered',
            delivered_at: deliveredAt,
          };
        };
        startTransition(() => {
          setMessages((prev) => prev.map(applyDelivered));
          if (deliveredConversationId) {
            setAllConversations((prev) => {
              const conv = prev.get(deliveredConversationId);
              if (!conv?.messages) return prev;
              const updated = new Map(prev);
              updated.set(deliveredConversationId, {
                ...conv,
                messages: conv.messages.map(applyDelivered),
              });
              allConversationsRef.current = updated;
              return updated;
            });
          }
        });
        break;
      }

      case 'resync': {
        const conversations = data?.conversations || [];
        conversations.forEach((page) => {
          const cid = page?.conversation_id;
          if (!cid) return;
          const incoming = (page.messages || []).map((msg) => formatMessageMemo(msg, USER_ID));
          noteFormattedSequences(cid, incoming);
          incoming.forEach((msg) => {
            if (msg.direction === 'incoming') ackDelivered(msg.id);
          });
          const isActive =
            conversationIdRef.current != null &&
            String(cid) === String(conversationIdRef.current);
          if (isActive) {
            startTransition(() => {
              setMessages((prev) => {
                let next = prev;
                incoming.forEach((msg) => {
                  next = mergeMessagesByIdentity(next, msg);
                });
                return next;
              });
            });
          }
          incoming.forEach((msg) => upsertConversationMessage(cid, msg));
        });
        break;
      }

      case 'typing': {
        const typingConvoId = data?.conversation_id;
        if (
          typingConvoId != null &&
          conversationIdRef.current != null &&
          String(typingConvoId) !== String(conversationIdRef.current)
        ) {
          break;
        }
        if (data?.user_id != null && String(data.user_id) !== String(USER_ID)) {
          setIsTyping(Boolean(data?.is_typing));
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          if (data?.is_typing) {
            typingTimeoutRef.current = setTimeout(() => {
              setIsTyping(false);
            }, 3000);
          }
        }
        break;
      }

      case 'read':
      case 'message_read': {
        const readMessageId = String(data?.message_id);
        const readConversationId = data?.conversation_id;
        const readAt = data?.read_at || new Date().toISOString();
        formatMessageCacheRef.current.delete(`${readMessageId}_${USER_ID}`);
        const applyRead = (msg) => {
          if (String(msg.id) !== readMessageId) return msg;
          if (msg.direction === 'outgoing') {
            readMessagesRef.current.add(readMessageId);
            return {
              ...msg,
              is_read: true,
              read_at: readAt,
              delivery_status: 'read',
            };
          }
          if (msg.direction === 'incoming' && !msg.is_read) {
            return {
              ...msg,
              is_read: true,
              read_at: readAt,
              delivery_status: 'read',
            };
          }
          return msg;
        };
        startTransition(() => {
          setMessages((prev) => prev.map(applyRead));
          if (readConversationId) {
            setAllConversations((prev) => {
              const conv = prev.get(readConversationId);
              if (!conv?.messages) return prev;
              const updated = new Map(prev);
              updated.set(readConversationId, {
                ...conv,
                messages: conv.messages.map(applyRead),
              });
              return updated;
            });
          }
        });
        break;
      }

      case 'error':
        break;

      default:
        break;
    }
  }, [
    USER_ID,
    handleNewMessage,
    sendJsonMessage,
    formatMessageMemo,
    clearUnreadForConversation,
    hydrateUnread,
    requestScrollToBottom,
    requestScrollPreserve,
    sendResyncCursor,
    noteFormattedSequences,
    noteMessageSequence,
    ackDelivered,
    upsertConversationMessage,
    mergeMessagesByIdentity,
  ]);

  useEffect(() => {
    return subscribeSocketEvents(handleSocketEvent);
  }, [subscribeSocketEvents, handleSocketEvent]);

  useEffect(() => {
    if (!conversationId || !isConnected) return;
    clearUnreadForConversation(conversationId);
    const convoKey = String(conversationId);
    if (!subscribedConversationsRef.current.has(convoKey)) {
      sendJsonMessage({ type: 'subscribe', conversation_id: conversationId, include_history: false });
      subscribedConversationsRef.current.add(convoKey);
    }
  }, [conversationId, isConnected, sendJsonMessage, clearUnreadForConversation]);

  const startConversation = useCallback((pmrn) => { if (pmrn) sendJsonMessage({ type: 'start_conversation', PMRN: pmrn }); }, [sendJsonMessage]);

  useEffect(() => {
    const fetchPatients = async () => {
      const token = getToken();
      if (!isTokenValid(token)) return;
      try {
        const response = await api.get('/patients/', { headers: { Authorization: `Bearer ${token}` } });
        setPatients(response.data.map((p) => ({ ...p, mrn: p.MRN || p.mrn, name: p.name || `${p.firstName || ''} ${p.lastName || ''}`.trim() })));
      } catch (error) { console.error('Failed to fetch patients:', error); }
    };
    fetchPatients();
  }, []);

  const handleSelectPatient = useCallback((patient) => {
    if (!patient.MRN) return;
    const mrn = patient.MRN ?? patient.mrn;
    const convMap = allConversationsRef.current;
    let cachedConversation = null;
    if (mrn != null && convMap) {
      let best = null;
      for (const [cid, conv] of convMap.entries()) {
        const pmrn = conv.patient_info?.PMRN ?? conv.patient_info?.pmrn ?? conv.PMRN;
        if (pmrn == null || String(pmrn) !== String(mrn)) continue;
        const candidate = { ...conv, conversation_id: conv.conversation_id ?? cid };
        const candidateCount = candidate.messages?.length || 0;
        const bestCount = best?.messages?.length || 0;
        if (!best || candidateCount > bestCount) best = candidate;
      }
      if (best) {
        clearUnreadForConversation(best.conversation_id);
        cachedConversation = best;
      }
    }
    setIsLoading(true);
    setSelectedPatient(patient);
    setMessages([]);
    setConversationId(null);
    setIsTyping(false);
    isUserScrollingRef.current = false;
    userScrolledAwayFromBottomRef.current = false;
    scrollIntentRef.current = null;
    resetScrollDownUi();
    loadingOlderRef.current = false;
    setIsLoadingOlder(false);
    pendingHistoryRef.current = null;
    setScrollDateLabel('');
    setShowScrollDate(false);
    if (isMobile) setShowChat(true);

    if (cachedConversation?.conversation_id) {
      setConversationId(cachedConversation.conversation_id);
      if (cachedConversation.messages?.length > 0) {
        setMessages(cachedConversation.messages);
        requestScrollToBottom();
      }
    }

    startConversation(patient.MRN);
    setIsLoading(false);
  }, [isMobile, startConversation, clearUnreadForConversation, requestScrollToBottom, resetScrollDownUi]);

  const getPatientUnreadCount = useCallback((patientMRN) => {
    const mrnStr = patientMRN != null ? String(patientMRN) : '';
    for (const [convoId, conv] of allConversations.entries()) {
      const convPmrn = conv.patient_info?.PMRN ?? conv.patient_info?.pmrn;
      if (convPmrn == null) continue;
      if (String(convPmrn) === mrnStr) return unreadByConversation?.get?.(convoId)?.count ?? 0;
    }
    return 0;
  }, [allConversations, unreadByConversation]);

  const handleBackToList = useCallback(() => setShowChat(false), []);
  const handleSearch = useCallback((query) => setSearchQuery(query), []);

  const handleTyping = useCallback((isCurrentlyTyping) => {
    if (!conversationId || !isConnected) return;
    const now = Date.now();
    if (now - lastTypingSentRef.current < 2000) return;
    lastTypingSentRef.current = now;
    sendJsonMessage({ type: 'typing', conversation_id: conversationId, is_typing: isCurrentlyTyping });
  }, [conversationId, isConnected, sendJsonMessage]);

  const handleSendMessage = useCallback((text) => {
    if (!text.trim() || !conversationId || !isConnected) return;
    const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    pendingLocalIdsRef.current.add(localId);
    const optimistic = {
      id: localId,
      local_id: localId,
      client_local_id: localId,
      conversation_id: conversationId,
      sender_id: USER_ID,
      sender_type: 'clinician',
      message_body: text,
      created_at: new Date().toISOString(),
      is_read: false,
      delivery_status: 'sent',
      sequence: null,
    };
    handleNewMessage(optimistic);
    sendJsonMessage({
      type: 'message',
      conversation_id: conversationId,
      message_body: text,
      message_type: 'text',
      recipient_id: null,
      recipient_type: null,
      is_urgent: false,
      local_id: localId,
    });
    requestScrollToBottom();
  }, [conversationId, isConnected, sendJsonMessage, requestScrollToBottom, USER_ID, handleNewMessage]);

  const handleScrollToBottomClick = useCallback(() => {
    scrollToBottomNow();
  }, [scrollToBottomNow]);

  const filteredPatients = useMemo(
    () => patients.filter(
      (p) => p.name?.toLowerCase().includes(searchQuery.toLowerCase()) || p.MRN?.toString().includes(searchQuery)
    ),
    [patients, searchQuery]
  );

  const sortPatientsByPriority = useCallback((list) => (
    [...list].sort((a, b) => {
      const unreadA = getPatientUnreadCount(a.MRN ?? a.mrn);
      const unreadB = getPatientUnreadCount(b.MRN ?? b.mrn);
      if (unreadA > 0 && unreadB === 0) return -1;
      if (unreadA === 0 && unreadB > 0) return 1;
      return (a.name || '').localeCompare(b.name || '');
    })
  ), [getPatientUnreadCount]);

  const onlinePatients = useMemo(
    () => sortPatientsByPriority(filteredPatients.filter((p) => isPatientOnline(p))),
    [filteredPatients, isPatientOnline, sortPatientsByPriority]
  );

  const offlinePatients = useMemo(
    () => sortPatientsByPriority(filteredPatients.filter((p) => !isPatientOnline(p))),
    [filteredPatients, isPatientOnline, sortPatientsByPriority]
  );

  const onlinePatientCount = useMemo(
    () => patients.filter((p) => isPatientOnline(p)).length,
    [patients, isPatientOnline]
  );

  const filteredOnlineCount = onlinePatients.length;
  const filteredOfflineCount = offlinePatients.length;

  const renderPatientListItem = useCallback((patient) => (
    <PatientListItem
      key={patient.MRN}
      patient={patient}
      isSelected={selectedPatient?.MRN === patient.MRN}
      isOnline={isPatientOnline(patient)}
      onClick={() => handleSelectPatient(patient)}
      isMobile={isMobile}
      unreadCount={getPatientUnreadCount(patient.MRN ?? patient.mrn)}
    />
  ), [selectedPatient?.MRN, isPatientOnline, handleSelectPatient, isMobile, getPatientUnreadCount]);

  const patientListContent = useMemo(() => {
    if (patientListFilter === 'online') {
      if (onlinePatients.length === 0) {
        return (
          <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
            <div style={{ width: '64px', height: '64px', backgroundColor: '#f0fdfa', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', border: '1px solid #ccfbf1' }}>
              <span style={{ width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#9ca3af', display: 'inline-block' }} />
            </div>
            <p style={{ fontWeight: '600', color: '#374151', margin: '0 0 6px' }}>No patients online</p>
            <p style={{ fontSize: '13px', margin: 0, lineHeight: 1.5 }}>
              {searchQuery ? 'Try a different search or switch to All patients.' : 'Online patients will appear here when they connect.'}
            </p>
          </div>
        );
      }

      return onlinePatients.map(renderPatientListItem);
    }

    if (filteredPatients.length === 0) {
      return (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: '#6b7280' }}>
          <div style={{ width: '60px', height: '60px', backgroundColor: '#f3f4f6', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}><SearchIcon /></div>
          <p style={{ fontWeight: '500' }}>No patients found</p>
          <p style={{ fontSize: '13px', marginTop: '4px' }}>Try a different search term</p>
        </div>
      );
    }

    return (
      <>
        {onlinePatients.length > 0 && (
          <>
            <PatientListSectionHeader title="Online now" count={filteredOnlineCount} variant="online" />
            {onlinePatients.map(renderPatientListItem)}
          </>
        )}
        {offlinePatients.length > 0 && (
          <>
            <PatientListSectionHeader title="Others" count={filteredOfflineCount} variant="offline" />
            {offlinePatients.map(renderPatientListItem)}
          </>
        )}
      </>
    );
  }, [
    patientListFilter,
    onlinePatients,
    offlinePatients,
    filteredPatients.length,
    filteredOnlineCount,
    filteredOfflineCount,
    searchQuery,
    renderPatientListItem,
  ]);

  const selectedPatientOnline = selectedPatient ? isPatientOnline(selectedPatient) : false;
  const sidebarWidth = isMobile ? '100%' : isTablet ? '300px' : '340px';

  return (
    <div className="vitra-atmosphere flex min-h-screen flex-col">
      <Header />
      <main style={{ flex: 1, padding: `${mainPadding}px`, maxWidth: '1400px', margin: '0 auto', width: '100%', boxSizing: 'border-box' }}>
        <div style={{ display: 'flex', height: `${Math.max(containerHeight, 500)}px`, backgroundColor: 'white', borderRadius: isMobile ? '16px' : '20px', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.08)', overflow: 'hidden', border: '1px solid #e5e7eb' }}>

          {/* ── Patient List Sidebar ── */}
          {(!isMobile || !showChat) && (
            <div style={{ width: sidebarWidth, minWidth: sidebarWidth, borderRight: isMobile ? 'none' : '1px solid #e5e7eb', display: 'flex', flexDirection: 'column', backgroundColor: 'white', height: '100%' }}>
              <div style={{ background: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', padding: isMobile ? '20px' : '24px', color: 'white' }}>
                <h2 style={{ fontSize: isMobile ? '20px' : '22px', fontWeight: '700', margin: 0, letterSpacing: '-0.5px' }}>Messages</h2>
                <p style={{ fontSize: '14px', color: 'rgba(255,255,255,0.8)', marginTop: '6px' }}>{patients.length} patient{patients.length !== 1 ? 's' : ''} • {onlinePatientCount} online</p>
              </div>
              <div style={{ padding: '16px 16px 8px', borderBottom: '1px solid #e5e7eb', backgroundColor: '#f9fafb', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <SearchInput onSearch={handleSearch} placeholder="Search patients..." />
                <PatientListFilter
                  value={patientListFilter}
                  onChange={setPatientListFilter}
                  allCount={filteredPatients.length}
                  onlineCount={filteredOnlineCount}
                  isMobile={isMobile}
                />
              </div>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {patientListContent}
              </div>
            </div>
          )}

          {/* ── Chat Area ── */}
          {(!isMobile || showChat) && (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, height: '100%', backgroundColor: '#f9fafb' }}>
              {selectedPatient ? (
                <>
                  <div style={{ padding: isMobile ? '14px 16px' : '16px 24px', borderBottom: '1px solid #e5e7eb', backgroundColor: 'white', display: 'flex', alignItems: 'center', gap: '14px', flexShrink: 0 }}>
                    {isMobile && (<button onClick={handleBackToList} style={{ background: 'none', border: 'none', padding: '8px', margin: '-8px 0 -8px -8px', cursor: 'pointer', color: '#0d9488', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '8px' }}><BackIcon /></button>)}
                    <CustomAvatar name={selectedPatient.name} size={isMobile ? 'default' : 'large'} isOnline={selectedPatientOnline} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '2px', minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontWeight: '600', fontSize: isMobile ? '16px' : '18px', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedPatient.name}</h3>
                        {isTyping && <HeaderTypingIndicator />}
                      </div>
                      <p style={{ margin: '2px 0 0', fontSize: '13px', color: '#6b7280' }}>MRN: {selectedPatient.MRN}</p>
                    </div>
                    <ConnectionBadge status={connectionStatus} isPatientOnline={selectedPatientOnline} />
                  </div>

                  <div style={{ position: 'relative', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    {showScrollDate && scrollDateLabel && messages.length > 0 && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '12px',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          zIndex: 20,
                          pointerEvents: 'none',
                          padding: '6px 14px',
                          borderRadius: '999px',
                          backgroundColor: 'rgba(17, 24, 39, 0.82)',
                          color: 'white',
                          fontSize: '12px',
                          fontWeight: '600',
                          letterSpacing: '0.2px',
                          boxShadow: '0 4px 14px rgba(0, 0, 0, 0.18)',
                          backdropFilter: 'blur(8px)',
                          animation: 'fadeInDatePill 0.2s ease-out',
                        }}
                      >
                        {scrollDateLabel}
                      </div>
                    )}

                    <div ref={messagesContainerRef} style={{ flex: 1, overflowY: 'auto', padding: isMobile ? '16px' : '24px', display: 'flex', flexDirection: 'column', scrollBehavior: 'auto' }}>
                    {isLoading ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}><div style={{ textAlign: 'center', color: '#6b7280' }}><div className="loading-spinner" /><p style={{ marginTop: '16px', fontWeight: '500' }}>Loading messages...</p></div></div>
                    ) : messages.length === 0 ? (
                      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                          <div style={{ width: isMobile ? '80px' : '100px', height: isMobile ? '80px' : '100px', background: 'linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', boxShadow: '0 8px 24px rgba(13, 148, 136, 0.2)' }}><ChatBubbleIcon size={isMobile ? 40 : 48} /></div>
                          <h3 style={{ fontSize: isMobile ? '18px' : '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                            {conversationId ? 'No messages yet' : 'Opening conversation…'}
                          </h3>
                          <p style={{ color: '#6b7280', fontSize: isMobile ? '14px' : '15px', maxWidth: '280px', margin: '0 auto', lineHeight: '1.6' }}>
                            {conversationId
                              ? `Send a message to ${selectedPatient.name}`
                              : 'Connecting to this chat…'}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {isLoadingOlder && (
                          <div style={{ textAlign: 'center', padding: '8px 0', color: '#6b7280', fontSize: '13px' }}>
                            Loading older messages...
                          </div>
                        )}
                        {messages.map((msg) => (<MessageBubble key={msg.id} msg={msg} isMobile={isMobile} />))}
                        <div ref={messagesEndRef} style={{ height: '1px' }} />
                      </div>
                    )}
                    </div>

                    <ScrollToBottomButton
                      visible={showScrollDownButton && messages.length > 0}
                      newMessageCount={pendingNewMessageCount}
                      onClick={handleScrollToBottomClick}
                      isMobile={isMobile}
                    />
                  </div>

                  <div style={{ padding: isMobile ? '12px 16px 16px' : '16px 24px 20px', backgroundColor: 'white', borderTop: '1px solid #e5e7eb', flexShrink: 0 }}>
                    <MessageInput onSend={handleSendMessage} onTyping={handleTyping} placeholder={`Message ${selectedPatient.name}...`} disabled={!conversationId || !isConnected} isMobile={isMobile} />
                  </div>
                </>
              ) : (
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
                  <div style={{ textAlign: 'center', padding: '60px 40px' }}>
                    <div style={{ width: '120px', height: '120px', background: 'linear-gradient(135deg, #ccfbf1 0%, #99f6e4 100%)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 32px', boxShadow: '0 12px 32px rgba(13, 148, 136, 0.2)' }}><MessageIcon size={56} /></div>
                    <h3 style={{ fontSize: '26px', fontWeight: '700', color: '#1f2937', marginBottom: '12px', letterSpacing: '-0.5px' }}>Welcome to Messages</h3>
                    <p style={{ color: '#6b7280', maxWidth: '320px', margin: '0 auto', fontSize: '16px', lineHeight: '1.6' }}>Select a patient from the list to start a secure, real-time conversation</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <Footer />
      <style>{`
        @keyframes fadeInDatePill { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scrollBtnIn { from { opacity: 0; transform: translateY(8px) scale(0.92); } to { opacity: 1; transform: translateY(0) scale(1); } }
        @keyframes badgePop { 0% { transform: scale(0.6); } 70% { transform: scale(1.12); } 100% { transform: scale(1); } }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: 0.7; transform: scale(1.1); } }
        @keyframes fadeSlideIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes typingBounce { 0%, 60%, 100% { transform: translateY(0); } 30% { transform: translateY(-6px); } }
        .loading-spinner { width: 48px; height: 48px; border: 4px solid #e5e7eb; border-top-color: #0d9488; border-radius: 50%; animation: spin 1s linear infinite; margin: 0 auto; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
        ::-webkit-scrollbar-thumb:hover { background: #9ca3af; }
        * { scroll-behavior: smooth; }
        button, a, div[onclick] { -webkit-tap-highlight-color: transparent; }
        input::placeholder { color: #9ca3af; }
        button:focus-visible, input:focus-visible { outline: 2px solid #0d9488; outline-offset: 2px; }
      `}</style>
    </div>
  );
}

export default MessagesPage;
